import type { OutreachOperation } from '@discovr/types';
import { failedTotal } from './operationOutcomes';
import { skipLabel } from './outreachSendCopy';

/** The find_contacts worker's own skip reasons (Go jobs/run_operation.go buildItem). */
const SKIP_LABELS: Record<string, string> = {
  already_has_contact: 'Already has an email',
  erased: 'Erased',
  lookup_unsupported: 'Instagram only',
};

const ERROR_LABELS: Record<string, string> = {
  NO_TARGETS: 'Everyone selected already has an email.',
  REQUEST_IN_PROGRESS: 'A contact search for this selection is already running.',
  OUTREACH_UNAVAILABLE: "Contact search isn't available right now — try again later.",
};

const contacts = (n: number) => `${n} ${n === 1 ? 'contact' : 'contacts'}`;

export function findContactsSkipLabel(reason: string): string {
  return SKIP_LABELS[reason] ?? skipLabel(reason);
}

export function findContactsStartedCopy(searching: number, alreadyHaveEmail: number): string {
  const head = `Looking for ${contacts(searching)}`;
  if (alreadyHaveEmail === 0) return head;
  return `${head} · ${alreadyHaveEmail} already ${alreadyHaveEmail === 1 ? 'has' : 'have'} an email`;
}

/** "applied" means handed to the agent, not found: found emails land on their rows over SSE. */
export function findContactsSummary(operation: OutreachOperation): string {
  const failed = failedTotal(operation);
  const settled = operation.appliedCount + operation.skippedCount + failed;
  if (settled === 0) return "Contact search isn't available right now — nothing was searched.";
  const skips = (operation.outcomes ?? [])
    .filter((o) => o.outcome === 'skipped')
    .reduce<Record<string, number>>((acc, o) => {
      const reason = o.reason || 'unknown';
      return { ...acc, [reason]: (acc[reason] ?? 0) + 1 };
    }, {});
  const detail = Object.entries(skips)
    .map(([reason, n]) => `${findContactsSkipLabel(reason)} ${n}`)
    .join(', ');
  return [
    operation.appliedCount > 0 || failed === 0 ? `Search started for ${contacts(operation.appliedCount)}` : 'No contact search started', // copy: ours
    ...(operation.skippedCount > 0 ? [`skipped ${operation.skippedCount}${detail ? ` (${detail})` : ''}`] : []),
    ...(failed > 0 ? [`failed ${failed}`] : []),
  ].join(' · ');
}

/** Nothing searched or nothing started is an error; any failure is a warning. */
export function findContactsToastKind(operation: OutreachOperation): 'success' | 'warning' | 'error' {
  if (operation.appliedCount === 0 && (failedTotal(operation) > 0 || operation.skippedCount === 0)) return 'error';
  return failedTotal(operation) > 0 ? 'warning' : 'success';
}

export function findContactsErrorCopy(code: string | undefined): string {
  return (code && ERROR_LABELS[code]) || "Couldn't start the contact search — try again.";
}
